/**
 * CSV: modelo, importação validada e exportação (leads fora de data/main).
 */

import { LEAD_STATUS, LEAD_STATUS_LABEL, LEAD_STATUS_ORDER } from './leadConstants.js';
import { ONBOARDING_STATUS_LABEL, getOnboardingEntrySourceLabel } from './onboardingConstants.js';
import { onlyDigits } from './leadsRepo.js';
import { resolveOrigemLeadForFirestore } from './origemLeadNormalize.js';

const PAD2 = n => String(n).padStart(2, '0');

/**
 * Excel / análise: data e hora em pt-BR (timezone local).
 * @param {unknown} ms
 */
export function formatCsvDateTime(ms) {
  if (ms == null || !Number.isFinite(Number(ms))) return '';
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return '';
  return `${PAD2(d.getDate())}/${PAD2(d.getMonth() + 1)}/${d.getFullYear()} ${PAD2(d.getHours())}:${PAD2(d.getMinutes())}`;
}

/** Data sem hora (ex.: nascimento). */
export function formatCsvDateOnly(ms) {
  if (ms == null || !Number.isFinite(Number(ms))) return '';
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return '';
  return `${PAD2(d.getDate())}/${PAD2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Trilean e booleanos para Excel: Sim / Não / vazio (sem registo). */
export function csvSimNao(value) {
  if (value === true) return 'Sim';
  if (value === false) return 'Não';
  return '';
}

/**
 * BR: (DD) NNNNN-NNNN ou (DD) NNNN-NNNN; senão dígitos ou texto original.
 */
export function formatCsvWhatsappBr(leadLike) {
  const d = onlyDigits(leadLike?.whatsappDigits) || onlyDigits(leadLike?.whatsapp);
  if (d.length === 11) {
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  }
  const disp = String(leadLike?.whatsapp ?? '').trim();
  return disp || d || '';
}

/**
 * Coluna «WhatsApp» no CSV: prioriza `whatsappMasked` do site (legível, alinhado ao que o lead viu).
 * Se não houver máscara, formata dígitos BR ou cai no texto legado.
 */
export function formatCsvWhatsappForExport(leadLike) {
  const masked = String(leadLike?.whatsappMasked ?? '').trim();
  if (masked) return masked;
  return formatCsvWhatsappBr(leadLike);
}

/** Cabeçalhos oficiais do modelo / importação (ordem estável; chaves = primeira linha do CSV). */
export const CRM_CSV_HEADERS = [
  'nome',
  'email',
  'whatsapp',
  'whatsappDigits',
  'cidade',
  'estado',
  'especialidade',
  'instagram',
  'origemLead',
  'source',
  'channel',
  'origem',
  'servicos',
  'mensagem',
  'responsavel',
  'status',
  'followUpAt',
  'origem_pagina',
];

/** Obrigatório na importação. */
export const CRM_CSV_REQUIRED = ['email'];

const STATUS_SLUGS = new Set(LEAD_STATUS_ORDER);

/** Rótulos PT e variações → slug Firestore. */
const STATUS_ALIASES = {
  novo: LEAD_STATUS.NOVO,
  'em contato': LEAD_STATUS.CONTATO,
  contato: LEAD_STATUS.CONTATO,
  'contato feito': LEAD_STATUS.CONTATO,
  'em negociação': LEAD_STATUS.NEGOCIACAO,
  negociacao: LEAD_STATUS.NEGOCIACAO,
  negociação: LEAD_STATUS.NEGOCIACAO,
  onboarding: LEAD_STATUS.ONBOARDING,
  'em onboarding': LEAD_STATUS.ONBOARDING,
  convertido: LEAD_STATUS.CONVERTIDO,
  fechado: LEAD_STATUS.CONVERTIDO,
  perdido: LEAD_STATUS.PERDIDO,
};

function normKey(s) {
  return String(s || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function parseStatus(raw) {
  const t = normKey(raw);
  if (!t) return { ok: true, value: LEAD_STATUS.NOVO };
  if (STATUS_SLUGS.has(t)) return { ok: true, value: t };
  const alias = STATUS_ALIASES[t];
  if (alias) return { ok: true, value: alias };
  return {
    ok: false,
    error: `Status inválido: "${raw}". Use: ${LEAD_STATUS_ORDER.map(s => LEAD_STATUS_LABEL[s]).join(', ')} ou slugs: ${LEAD_STATUS_ORDER.join(', ')}.`,
  };
}

function parseFollowUp(raw) {
  const s = String(raw || '').trim();
  if (!s) return { ok: true, value: null };
  const iso = Date.parse(s);
  if (!Number.isNaN(iso)) return { ok: true, value: iso };
  const br = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
  if (br) {
    const d = Number(br[1]);
    const m = Number(br[2]) - 1;
    const y = Number(br[3]);
    const hh = br[4] != null ? Number(br[4]) : 9;
    const mm = br[5] != null ? Number(br[5]) : 0;
    const dt = new Date(y, m, d, hh, mm, 0, 0);
    if (!Number.isNaN(dt.getTime())) return { ok: true, value: dt.getTime() };
  }
  return {
    ok: false,
    error: `Data follow-up não reconhecida: "${raw}" (use DD/MM/AAAA, DD/MM/AAAA HH:mm ou ISO).`,
  };
}

/**
 * Lista de serviços a partir de célula CSV: `;`, `|` ou `,` (com espaços).
 * Suporta exportação com separador por vírgula para legibilidade no Excel.
 */
function parseServicos(raw) {
  if (raw == null || raw === '') return [];
  const s = String(raw).trim();
  if (!s) return [];
  return s
    .split(/\s*[,;|]\s*/)
    .map(x => x.trim())
    .filter(Boolean);
}

function basicEmailOk(email) {
  const e = String(email || '').trim().toLowerCase();
  if (!e) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

/**
 * Parser CSV simples (aspas RFC4180 básico).
 * @param {string} text
 * @returns {string[][]}
 */
export function parseCSV(text) {
  const rows = [];
  let row = [];
  let cur = '';
  let inQ = false;
  const str = String(text || '');
  for (let i = 0; i < str.length; i++) {
    const c = str[i];
    if (inQ) {
      if (c === '"') {
        if (str[i + 1] === '"') {
          cur += '"';
          i++;
        } else inQ = false;
      } else cur += c;
    } else if (c === '"') {
      inQ = true;
    } else if (c === ',') {
      row.push(cur);
      cur = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && str[i + 1] === '\n') i++;
      row.push(cur);
      const nonEmpty = row.some(cell => String(cell).trim() !== '');
      if (nonEmpty) rows.push(row);
      row = [];
      cur = '';
    } else cur += c;
  }
  row.push(cur);
  if (row.some(cell => String(cell).trim() !== '')) rows.push(row);
  return rows;
}

function escapeCsvCell(val) {
  const s = val == null ? '' : String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function stringifyCSV(headers, dataRows) {
  const lines = [
    headers.map(escapeCsvCell).join(','),
    ...dataRows.map(r => headers.map(h => escapeCsvCell(r[h] ?? '')).join(',')),
  ];
  return lines.join('\r\n');
}

/**
 * Exportação humana: primeira linha = rótulos PT; linhas seguintes usam `key` de cada coluna.
 * @param {{ key: string, header: string }[]} columns
 * @param {Record<string, unknown>[]} dataRows
 */
export function stringifyExportCSV(columns, dataRows) {
  const keys = columns.map(c => c.key);
  const labels = columns.map(c => c.header);
  const lines = [
    labels.map(escapeCsvCell).join(','),
    ...dataRows.map(r => keys.map(k => escapeCsvCell(r[k] ?? '')).join(',')),
  ];
  return lines.join('\r\n');
}

/** Linha de exemplo + comentários # (ignorados na importação). Alinhado ao parser e à exportação atual. */
export function buildTemplateCsvContent() {
  const comment = [
    '# Messa CRM — Modelo de importação de leads',
    '# Obrigatório: email. Demais colunas opcionais.',
    '# Serviços (servicos): separar com vírgula, ponto e vírgula ou barra. Valor canónico do site para pacote completo: "Completo" (não usar "Todos").',
    '# Follow-up (followUpAt): DD/MM/AAAA ou DD/MM/AAAA HH:mm (ex.: 20/04/2025 10:30). Não usar timestamp Unix no modelo. Também aceita data ISO.',
    '# Booleanos (quando existirem em CSV de exportação ou extensões): Sim ou Não — não usar true/false.',
    '# Status: slugs (novo, contato, negociacao, convertido, perdido) ou rótulos em português (Novo, Em contato, …).',
    '# WhatsApp: alinhar à exportação — preferir máscara do site na coluna whatsapp (ex.: (11) 9****-0000); whatsappDigits só dígitos (11 dígitos típ.) para consistência/dedupe.',
    '# Responsável: e-mail, UID ou nome da equipa (utilizadores do dashboard).',
    '# Consentimentos, duplicado, userAgent e outras colunas técnicas não fazem parte deste modelo — vêm do site ou do CSV de exportação completa.',
    '# origemLead: slug canónico (ex.: instagram, google, meta_ads, google_ads, indicacao, outros, site_direto). Sinónimos (insta, facebook_ads, …) são normalizados na importação.',
    '# Se origemLead estiver vazio: usa-se source (quando mapeável); caso contrário grava-se site_direto.',
  ].join('\r\n');
  const example = {
    nome: 'Maria Silva (exemplo)',
    email: 'maria.exemplo@clinica.com',
    whatsapp: '(11) 9****-0000',
    whatsappDigits: '11999990000',
    cidade: 'São Paulo',
    estado: 'SP',
    especialidade: 'Dermatologia',
    instagram: '@mariaexemplo',
    origemLead: 'instagram',
    source: 'instagram',
    channel: '',
    origem: 'import_csv',
    servicos: 'Completo',
    mensagem: 'Interessada em plano anual.',
    responsavel: '',
    status: 'Novo',
    followUpAt: '20/04/2025 10:30',
    origem_pagina: 'modelo_importacao',
  };
  return `${comment}\r\n${stringifyCSV(CRM_CSV_HEADERS, [example])}\r\n`;
}

function downloadBlob(filename, mime, content) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadCrmTemplateCsv() {
  downloadBlob('messa-crm-leads-modelo.csv', 'text/csv;charset=utf-8', buildTemplateCsvContent());
}

/** Colunas extra na exportação (site / compliance); não obrigatórias na importação — chaves estáveis. */
export const CRM_CSV_EXPORT_EXTRA = [
  'whatsappDigits',
  'whatsappMasked',
  'formType',
  'receivedAt',
  'privacyConsent',
  'privacyConsentAt',
  'privacyConsentSource',
  'marketingConsent',
  'marketingConsentAt',
  'marketingConsentSource',
  'source',
  'channel',
  'duplicate',
];

/** Ordem e cabeçalhos PT na exportação de leads (análise / Excel). */
export const CRM_LEAD_EXPORT_COLUMNS = [
  { key: 'nome', header: 'Nome' },
  { key: 'email', header: 'E-mail' },
  { key: 'whatsapp', header: 'WhatsApp' },
  { key: 'cidade', header: 'Cidade' },
  { key: 'estado', header: 'UF' },
  { key: 'especialidade', header: 'Especialidade' },
  { key: 'instagram', header: 'Instagram' },
  { key: 'origemLead', header: 'Origem do lead (canal)' },
  { key: 'origem', header: 'Origem' },
  { key: 'servicos', header: 'Serviços de interesse' },
  { key: 'mensagem', header: 'Mensagem' },
  { key: 'responsavel', header: 'Responsável' },
  { key: 'status', header: 'Status' },
  { key: 'followUpAt', header: 'Follow-up' },
  { key: 'origem_pagina', header: 'Página de origem' },
  { key: 'source', header: 'Source' },
  { key: 'channel', header: 'Canal' },
  { key: 'whatsappDigits', header: 'WhatsApp (só dígitos)' },
  { key: 'formType', header: 'Tipo de formulário' },
  { key: 'receivedAt', header: 'Recebido em (site)' },
  { key: 'privacyConsent', header: 'Consentimento privacidade' },
  { key: 'privacyConsentAt', header: 'Privacidade (data/hora)' },
  { key: 'privacyConsentSource', header: 'Privacidade (origem do registo)' },
  { key: 'marketingConsent', header: 'Consentimento marketing' },
  { key: 'marketingConsentAt', header: 'Marketing (data/hora)' },
  { key: 'marketingConsentSource', header: 'Marketing (origem do registo)' },
  { key: 'duplicate', header: 'Marcado duplicado (site)' },
  { key: 'createdAt', header: 'Criado em' },
  { key: 'updatedAt', header: 'Atualizado em' },
  { key: 'id', header: 'ID (Firestore)' },
  { key: 'arquivado', header: 'Arquivado' },
];

/** @param {Record<string, unknown>} lead — lead normalizado da lista */
export function leadToExportRow(lead) {
  const st = lead.status && LEAD_STATUS_LABEL[/** @type {string} */ (lead.status)];
  return {
    nome: lead.nome ?? '',
    email: lead.email ?? '',
    whatsapp: formatCsvWhatsappForExport(lead),
    cidade: lead.cidade ?? '',
    estado: lead.estado ?? '',
    especialidade: lead.especialidade ?? '',
    instagram: lead.instagram ?? '',
    origemLead: lead.origemLead ?? '',
    origem: lead.origem ?? '',
    servicos: Array.isArray(lead.servicos) ? lead.servicos.join(', ') : String(lead.servicos ?? ''),
    mensagem: lead.mensagem ?? '',
    responsavel: lead.responsavelName || lead.responsavelUid || '',
    status: st || String(lead.status ?? ''),
    followUpAt: formatCsvDateTime(lead.followUpAt),
    origem_pagina: lead.origem_pagina ?? '',
    whatsappDigits: lead.whatsappDigits || '',
    formType: lead.formType || '',
    receivedAt: formatCsvDateTime(lead.receivedAt),
    privacyConsent: lead.privacyConsent === true ? 'Sim' : lead.privacyConsent === false ? 'Não' : '',
    privacyConsentAt: formatCsvDateTime(lead.privacyConsentAt),
    privacyConsentSource: lead.privacyConsentSource || '',
    marketingConsent: lead.marketingConsent === true ? 'Sim' : lead.marketingConsent === false ? 'Não' : '',
    marketingConsentAt: formatCsvDateTime(lead.marketingConsentAt),
    marketingConsentSource: lead.marketingConsentSource || '',
    source: lead.source || '',
    channel: lead.channel || '',
    duplicate: lead.duplicate === true ? 'Sim' : 'Não',
    createdAt: formatCsvDateTime(lead.createdAt),
    updatedAt: formatCsvDateTime(lead.updatedAt),
    id: lead.id,
    arquivado: lead.archived === true ? 'Sim' : 'Não',
  };
}

export function exportLeadsToCsv(leadsFiltered, filename = 'messa-crm-leads-export.csv') {
  const rows = leadsFiltered.map(leadToExportRow);
  const content = stringifyExportCSV(CRM_LEAD_EXPORT_COLUMNS, rows);
  downloadBlob(filename, 'text/csv;charset=utf-8', '\uFEFF' + content);
}

/** Colunas export onboarding (PT). */
export const CRM_ONBOARDING_EXPORT_COLUMNS = [
  { key: 'nomeCompleto', header: 'Nome completo' },
  { key: 'dataNascimento', header: 'Data de nascimento' },
  { key: 'cpf', header: 'CPF' },
  { key: 'cnpj', header: 'CNPJ' },
  { key: 'email', header: 'E-mail' },
  { key: 'whatsapp', header: 'WhatsApp' },
  { key: 'whatsappDigits', header: 'WhatsApp (só dígitos)' },
  { key: 'enderecoResidencial', header: 'Endereço residencial' },
  { key: 'enderecoProfissional', header: 'Endereço profissional' },
  { key: 'cidade', header: 'Cidade' },
  { key: 'estado', header: 'UF' },
  { key: 'especialidade', header: 'Especialidade' },
  { key: 'crm', header: 'CRM' },
  { key: 'rqe', header: 'RQE' },
  { key: 'status', header: 'Status' },
  { key: 'formType', header: 'Tipo de formulário' },
  { key: 'receivedAt', header: 'Recebido em (site)' },
  { key: 'origem', header: 'Origem' },
  { key: 'source', header: 'Source' },
  { key: 'channel', header: 'Canal' },
  { key: 'leadId', header: 'ID lead vinculado' },
  { key: 'provenienciaCrm', header: 'Proveniência (CRM)' },
  { key: 'plano', header: 'Plano (JSON)' },
  { key: 'tipoFinal', header: 'Tipo final' },
  { key: 'reviewedAt', header: 'Revisto em' },
  { key: 'privacyConsent', header: 'Consentimento privacidade' },
  { key: 'privacyConsentAt', header: 'Privacidade (data/hora)' },
  { key: 'privacyConsentSource', header: 'Privacidade (origem do registo)' },
  { key: 'marketingConsent', header: 'Consentimento marketing' },
  { key: 'marketingConsentAt', header: 'Marketing (data/hora)' },
  { key: 'marketingConsentSource', header: 'Marketing (origem do registo)' },
  { key: 'userAgent', header: 'User-Agent' },
  { key: 'createdAt', header: 'Criado em' },
  { key: 'updatedAt', header: 'Atualizado em' },
  { key: 'arquivado', header: 'Arquivado' },
  { key: 'archivedAt', header: 'Arquivado em' },
  { key: 'convertidoEm', header: 'Convertido em' },
  { key: 'idCliente', header: 'ID cliente (pós-conversão)' },
  { key: 'id', header: 'ID (Firestore)' },
];

/** @param {Record<string, unknown>} ob — onboarding normalizado */
export function onboardingToExportRow(ob) {
  const st = ob.status && ONBOARDING_STATUS_LABEL[/** @type {string} */ (ob.status)];
  let planoStr = '';
  try {
    const p = ob.plano;
    planoStr =
      p && typeof p === 'object' && !Array.isArray(p) && Object.keys(p).length
        ? JSON.stringify(p)
        : '';
  } catch {
    planoStr = '';
  }
  return {
    nomeCompleto: ob.nomeCompleto ?? '',
    dataNascimento: formatCsvDateOnly(ob.dataNascimento),
    cpf: ob.cpf ?? '',
    cnpj: ob.cnpj ?? '',
    email: ob.email ?? '',
    whatsapp: formatCsvWhatsappBr(ob),
    whatsappDigits: ob.whatsappDigits || '',
    enderecoResidencial: ob.enderecoResidencial ?? '',
    enderecoProfissional: ob.enderecoProfissional ?? '',
    cidade: ob.cidade ?? '',
    estado: ob.estado ?? '',
    especialidade: ob.especialidade ?? '',
    crm: ob.crm ?? '',
    rqe: ob.rqe ?? '',
    status: st || String(ob.status ?? ''),
    formType: ob.formType || '',
    receivedAt: formatCsvDateTime(ob.receivedAt),
    origem: ob.origem ?? '',
    source: ob.source || '',
    channel: ob.channel || '',
    leadId: ob.leadId || '',
    provenienciaCrm: getOnboardingEntrySourceLabel(ob),
    plano: planoStr,
    tipoFinal: ob.tipoFinal || '',
    reviewedAt: formatCsvDateTime(ob.reviewedAt),
    privacyConsent: csvSimNao(ob.privacyConsent),
    privacyConsentAt: formatCsvDateTime(ob.privacyConsentAt),
    privacyConsentSource: ob.privacyConsentSource || '',
    marketingConsent: csvSimNao(ob.marketingConsent),
    marketingConsentAt: formatCsvDateTime(ob.marketingConsentAt),
    marketingConsentSource: ob.marketingConsentSource || '',
    userAgent: ob.userAgent || '',
    createdAt: formatCsvDateTime(ob.createdAt),
    updatedAt: formatCsvDateTime(ob.updatedAt),
    arquivado: ob.archived === true ? 'Sim' : 'Não',
    archivedAt: formatCsvDateTime(ob.archivedAt),
    convertidoEm: formatCsvDateTime(ob.convertedAt),
    idCliente: ob.convertedClientId || '',
    id: ob.id,
  };
}

export function exportOnboardingToCsv(listFiltered, filename = 'messa-crm-onboarding-export.csv') {
  const rows = listFiltered.map(onboardingToExportRow);
  const content = stringifyExportCSV(CRM_ONBOARDING_EXPORT_COLUMNS, rows);
  downloadBlob(filename, 'text/csv;charset=utf-8', '\uFEFF' + content);
}

/**
 * Resolve "responsavel" (texto) → { uid, name } ou erro.
 * @param {string} raw
 * @param {Array<{uid: string, displayName?: string, name?: string, email?: string}>} teamList
 */
export function matchResponsavel(raw, teamList) {
  const t = String(raw || '').trim();
  if (!t) return { ok: true, uid: '', name: '' };
  const lower = t.toLowerCase();
  const byEmail = teamList.find(u => (u.email || '').trim().toLowerCase() === lower);
  if (byEmail) {
    const name = (byEmail.displayName || byEmail.name || byEmail.email || '').trim();
    return { ok: true, uid: byEmail.uid, name };
  }
  const byUid = teamList.find(u => u.uid === t);
  if (byUid) {
    const name = (byUid.displayName || byUid.name || byUid.email || '').trim();
    return { ok: true, uid: byUid.uid, name };
  }
  const byName = teamList.find(u => {
    const n = (u.displayName || u.name || '').trim().toLowerCase();
    return n && (n === lower || n.includes(lower) || lower.includes(n));
  });
  if (byName) {
    const name = (byName.displayName || byName.name || byName.email || '').trim();
    return { ok: true, uid: byName.uid, name };
  }
  return {
    ok: false,
    error: `Responsável não encontrado: "${raw}". Use e-mail, UID ou nome exato da equipa.`,
  };
}

/**
 * @param {string[][]} rows — inclui cabeçalho
 * @param {object} ctx
 * @param {Array<{id: string, email: string}>} ctx.existingLeads — id + email lower
 * @param {Array<{uid: string, displayName?: string, name?: string, email?: string}>} ctx.teamList
 * @param {{ updateDuplicates?: boolean }} [opts] — se true, leads com e-mail existente entram em toUpdate; senão em skipped
 */
export function validateImportRows(rows, ctx, opts = {}) {
  const updateDuplicates = opts.updateDuplicates === true;
  const { existingLeads, teamList } = ctx;
  const emailToLeadId = new Map();
  for (const l of existingLeads) {
    const em = String(l.email || '')
      .trim()
      .toLowerCase();
    if (em) emailToLeadId.set(em, l.id);
  }

  const filteredRows = rows.filter(r => {
    const first = String(r[0] != null ? r[0] : '').trim();
    if (first.startsWith('#')) return false;
    return true;
  });

  if (!filteredRows.length) {
    return {
      ok: false,
      errors: [{ line: 0, message: 'Ficheiro vazio ou só comentários.' }],
      toCreate: [],
      toUpdate: [],
      skipped: [],
    };
  }

  const headerRaw = filteredRows[0].map(c => normKey(c));
  const headerMap = {};
  headerRaw.forEach((h, i) => {
    if (h) headerMap[h] = i;
  });

  const missingReq = CRM_CSV_REQUIRED.filter(req => headerMap[req] === undefined);
  if (missingReq.length) {
    return {
      ok: false,
      errors: [
        {
          line: 1,
          message: `Cabeçalho em falta: ${missingReq.join(', ')}. Colunas esperadas: ${CRM_CSV_HEADERS.join(', ')}.`,
        },
      ],
      toCreate: [],
      toUpdate: [],
      skipped: [],
    };
  }

  const errors = [];
  const toCreate = [];
  const toUpdate = [];
  const skipped = [];
  const seenInFile = new Set();

  for (let r = 1; r < filteredRows.length; r++) {
    const lineNum = r + 1;
    const cells = filteredRows[r];
    const get = key => {
      const idx = headerMap[normKey(key)];
      if (idx === undefined) return '';
      return cells[idx] != null ? String(cells[idx]).trim() : '';
    };

    const email = get('email').toLowerCase();
    if (!email) {
      errors.push({ line: lineNum, message: 'E-mail obrigatório em falta.' });
      continue;
    }
    if (!basicEmailOk(email)) {
      errors.push({ line: lineNum, message: `E-mail inválido: "${email}".` });
      continue;
    }

    if (seenInFile.has(email)) {
      errors.push({ line: lineNum, message: `E-mail duplicado no ficheiro (já usado numa linha anterior): ${email}.` });
      continue;
    }
    seenInFile.add(email);

    const st = parseStatus(get('status'));
    if (!st.ok) {
      errors.push({ line: lineNum, message: st.error });
      continue;
    }

    const fu = parseFollowUp(get('followUpAt'));
    if (!fu.ok) {
      errors.push({ line: lineNum, message: fu.error });
      continue;
    }

    const resp = matchResponsavel(get('responsavel'), teamList);
    if (!resp.ok) {
      errors.push({ line: lineNum, message: resp.error });
      continue;
    }

    const waImp = get('whatsapp');
    const waDigCol = get('whatsappdigits') || get('whatsapp_digits');
    const srcCol = get('source') || get('canal');
    const chCol = get('channel');
    const olRaw = get('origemlead') || get('origem_lead');
    const payload = {
      nome: get('nome'),
      email,
      whatsapp: waImp,
      whatsappDigits: onlyDigits(waDigCol) || onlyDigits(waImp),
      cidade: get('cidade'),
      estado: get('estado'),
      especialidade: get('especialidade'),
      instagram: get('instagram'),
      source: srcCol,
      channel: chCol,
      origemLead: resolveOrigemLeadForFirestore(olRaw, srcCol),
      origem: get('origem') || 'import_csv',
      servicos: parseServicos(get('servicos')),
      mensagem: get('mensagem'),
      responsavelUid: resp.uid,
      responsavelName: resp.name,
      status: st.value,
      followUpAt: fu.value,
      origem_pagina: get('origem_pagina') || 'import_csv',
      entrada: 'import',
    };

    const existingId = emailToLeadId.get(email);
    if (existingId) {
      if (updateDuplicates) {
        toUpdate.push({ line: lineNum, leadId: existingId, payload });
      } else {
        skipped.push({
          line: lineNum,
          email,
          reason: 'E-mail já existe no CRM — linha ignorada. Ative «Atualizar existentes» para aplicar alterações.',
        });
      }
    } else {
      toCreate.push({ line: lineNum, payload });
    }
  }

  return {
    errors,
    toCreate,
    toUpdate,
    skipped,
    totalLines: filteredRows.length - 1,
  };
}
